import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import { UserStorageService } from './user-storage.service';

/**
 * Manages the QA backdoor token that gates the test:email@test.com login
 * (see server.ts's `authenticate` decorator). Stored as a device-level
 * (non-user-scoped) localStorage value since it's needed before any user is
 * authenticated -- it's what unlocks the ability to log in at all via the
 * test-login tab.
 */
@Injectable({ providedIn: 'root' })
export class QaBackdoorService {
  private http = inject(HttpClient);
  private userStorage = inject(UserStorageService);
  private logger = inject(LoggerService).getLogger('qa-backdoor');

  private readonly STORAGE_KEY = 'qa_backdoor_token';

  getToken(): string | null {
    return this.userStorage.getGlobalItem(this.STORAGE_KEY);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  clear(): void {
    this.userStorage.removeGlobalItem(this.STORAGE_KEY);
  }

  /** Validates the candidate token against the backend; saves it only if valid. */
  async validateAndSave(candidateToken: string): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ valid: boolean }>(
          `${environment.apiUrl}/qa/validate-backdoor-token`,
          {},
          { headers: { 'x-qa-backdoor-token': candidateToken } },
        ),
      );

      if (result.valid) {
        this.userStorage.setGlobalItem(this.STORAGE_KEY, candidateToken);
        return true;
      }
      return false;
    } catch (err) {
      this.logger.error('Failed to validate QA backdoor token:', err);
      return false;
    }
  }

  /**
   * Re-checks a previously-saved token against the backend and reports
   * whether it's still good -- clearing it if the server has confirmed it
   * can never validate again. hasToken() alone only checks presence in
   * localStorage, not correctness, so a stale value would otherwise keep the
   * test-login tab visible forever with no way for the backend to reject it.
   *
   * The backdoor is only considered available while online: the test-login
   * flow it gates requires hitting the real backend regardless (it issues a
   * JWT), so there's nothing to offer offline, and a cached "it worked
   * before" isn't something we can stand behind without asking the server.
   *
   * A confirmed 404 (test auth disabled, or no backdoor token configured at
   * all) means this token can never become valid again, so it's cleared.
   * Being offline, or any other failure (timeout, 5xx), is inconclusive --
   * reported as unavailable for now, but the stored token is left alone
   * rather than wiped over what may just be a network hiccup.
   */
  async hasValidToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    if (!navigator.onLine) {
      return false;
    }

    try {
      const result = await firstValueFrom(
        this.http.post<{ valid: boolean }>(
          `${environment.apiUrl}/qa/validate-backdoor-token`,
          {},
          { headers: { 'x-qa-backdoor-token': token } },
        ),
      );

      if (!result.valid) {
        this.clear();
      }
      return result.valid;
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        this.clear();
        return false;
      }
      this.logger.warn('Could not confirm QA backdoor token validity:', err);
      return false;
    }
  }
}
